import { Injectable } from '@angular/core';
import { ProgramItem } from '@app/app/page-modules/programs/models/program.model';
import { Page } from '@app/app/shared/models/page';
import {
  CohortQuestionDistributionsResponse,
  ProgramSurveyAnalyticsDTO,
  ProgramSurveysListResponse,
  SurveyCourseDetailDTO,
} from '@app/app/shared/services/survey-analytics/survey-analytics.model';
import { ObservableResult } from '@app/app/shared/store';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  AdminCoursesRequest,
  CohortGradeData,
  CohortOption,
  ProgramCourseItem,
  ProgramCoursesAnalyticsFilters,
  ProgramCoursesOverview,
  ProgramSkillsOverviewResponse,
  StudentEngagementData,
} from '../../models/courses-analytics.model';
import { CoursesAnalyticsDataService } from './courses-analytics-data.service';

/**
 * Mock counterpart to {@link ApiCourseAnalyticsDataService}. Mirrors the
 * single-course analytics shape so the admin course analytics tab renders with
 * `environment.dataProvider === 'mock'`.
 */
@Injectable()
export class MockCourseAnalyticsDataService implements CoursesAnalyticsDataService {
  private mockOverview: ProgramCoursesOverview = {
    members: 18,
    averageGrade: 72,
    averageGradeChange: 3,
    completion: 72,
    npsScore: 8.8,
    coursesCount: 1,
    activeCourses: 1,
    averagePreAssessment: 58,
    averagePostAssessment: 74,
    averageImprovement: 16,
    totalEnrolled: 18,
    totalInProgress: 5,
    totalCompleted: 13,
    surveyMetrics: [
      { metricType: 'NPS_SCORE', displayName: 'NPS Score', averageScore: 8.8, totalResponses: 15 },
      { metricType: 'PROFESSOR_RATING', displayName: 'Professor Rating', averageScore: 4.3, totalResponses: 16 },
      { metricType: 'COURSE_DESIGN_RATING', displayName: 'Course Design', averageScore: 3.8, totalResponses: 14 },
      { metricType: 'OVERALL_SATISFACTION', displayName: 'Overall Satisfaction', averageScore: 4.0, totalResponses: 16 },
    ],
    gradeDistribution: {
      totalStudents: 18,
      mean: 79.4,
      standardDeviation: 9.6,
    },
  };

  private mockCohortGradeData: CohortGradeData[] = [
    {
      label: 'Module 1',
      cohorts: [
        { name: 'ZAIN 2024 S7', value: 82.5 },
        { name: 'ARAMCO S2', value: 78.3 },
      ],
    },
    {
      label: 'Module 2',
      cohorts: [
        { name: 'ZAIN 2024 S7', value: 75.2 },
        { name: 'ARAMCO S2', value: 80.1 },
      ],
    },
  ];

  private mockEngagement: StudentEngagementData = {
    totalTimeSpent: 18000,
    uniqueStudents: 18,
    averageTimeSpentPerUser: 1000,
  };

  private mockCohorts: CohortOption[] = [
    { id: 'ZAIN 2024 S7', name: 'ZAIN 2024 S7', company: 'ZAIN', courseCount: 1, dateRange: 'Jan 2024 - Jun 2024' },
    { id: 'ARAMCO S2', name: 'ARAMCO S2', company: 'ARAMCO', courseCount: 1, dateRange: 'Feb 2024 - Jul 2024' },
  ];

  getAdminCourses(_params?: AdminCoursesRequest): ObservableResult<Page<ProgramItem>> {
    return ObservableResult.ofSuccess();
  }

  getOverview(_courseId: string, _filters?: ProgramCoursesAnalyticsFilters): ObservableResult<ProgramCoursesOverview> {
    return of({ isSuccess: true, value: this.mockOverview }).pipe(delay(300)) as ObservableResult<ProgramCoursesOverview>;
  }

  getProgramSkillsOverview(_courseId: string): ObservableResult<ProgramSkillsOverviewResponse> {
    const mock: ProgramSkillsOverviewResponse = { totalMembers: 0, skills: [], skillsByModule: [] };
    return of({ isSuccess: true, value: mock }).pipe(delay(300)) as ObservableResult<ProgramSkillsOverviewResponse>;
  }

  getOverviewByOrgId(
    _clientOrgId: string,
    courseId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<ProgramCoursesOverview> {
    return this.getOverview(courseId, filters);
  }

  getCourses(_courseId: string, _filters?: ProgramCoursesAnalyticsFilters): ObservableResult<ProgramCourseItem[]> {
    return of({ isSuccess: true, value: [] }).pipe(delay(300)) as ObservableResult<ProgramCourseItem[]>;
  }

  getCoursesByOrgId(
    _clientOrgId: string,
    courseId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<ProgramCourseItem[]> {
    return this.getCourses(courseId, filters);
  }

  getCohortGradeComparison(_courseId: string, _filters?: ProgramCoursesAnalyticsFilters): ObservableResult<CohortGradeData[]> {
    return of({ isSuccess: true, value: this.mockCohortGradeData }).pipe(delay(300)) as ObservableResult<CohortGradeData[]>;
  }

  getCohortGradeComparisonByOrgId(
    _clientOrgId: string,
    courseId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<CohortGradeData[]> {
    return this.getCohortGradeComparison(courseId, filters);
  }

  getStudentEngagement(_courseId: string, _filters?: ProgramCoursesAnalyticsFilters): ObservableResult<StudentEngagementData> {
    return of({ isSuccess: true, value: this.mockEngagement }).pipe(delay(300)) as ObservableResult<StudentEngagementData>;
  }

  getStudentEngagementByOrgId(
    _clientOrgId: string,
    courseId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<StudentEngagementData> {
    return this.getStudentEngagement(courseId, filters);
  }

  getCohorts(_courseId: string): ObservableResult<CohortOption[]> {
    return of({ isSuccess: true, value: this.mockCohorts }).pipe(delay(200)) as ObservableResult<CohortOption[]>;
  }

  getCohortsByOrgId(_clientOrgId: string, courseId: string): ObservableResult<CohortOption[]> {
    return this.getCohorts(courseId);
  }

  getSurveyAnalyticsByOrgId(
    _clientOrgId: string,
    _courseId: string,
    _filters?: { cohortNames?: string[]; metricType?: string; company?: string },
  ): ObservableResult<ProgramSurveyAnalyticsDTO> {
    return ObservableResult.ofSuccess();
  }

  getProgramSurveysListByOrgId(_clientOrgId: string, _courseId: string, _client?: string): ObservableResult<ProgramSurveysListResponse> {
    return ObservableResult.ofSuccess();
  }

  getQuestionDistributionsByOrgId(
    _clientOrgId: string,
    _courseId: string,
    _filters?: { cohortNames?: string[]; courseNames?: string[]; company?: string },
  ): ObservableResult<CohortQuestionDistributionsResponse> {
    return ObservableResult.ofSuccess();
  }

  getCourseDetailByOrgId(
    _clientOrgId: string,
    _courseId: string,
    _courseName: string,
    _cohortNames?: string[],
    _company?: string,
  ): ObservableResult<SurveyCourseDetailDTO> {
    return ObservableResult.ofSuccess();
  }

  getSurveyAnalytics(
    _id: string,
    _filters?: { cohortNames?: string[]; metricType?: string; company?: string },
  ): ObservableResult<ProgramSurveyAnalyticsDTO> {
    return ObservableResult.ofSuccess();
  }

  getSurveyCourseDetail(
    _id: string,
    _courseName: string,
    _cohortNames?: string[],
    _company?: string,
  ): ObservableResult<SurveyCourseDetailDTO> {
    return ObservableResult.ofSuccess();
  }

  getCohortQuestionDistributions(
    _id: string,
    _filters?: { cohortNames?: string[]; courseNames?: string[]; company?: string },
  ): ObservableResult<CohortQuestionDistributionsResponse> {
    return ObservableResult.ofSuccess();
  }
}
